import React, { useMemo } from 'react';
import { useCurrentFrame, useVideoConfig } from 'remotion';
import {
  calculateAnimationsStyle,
  mergeAnimationStyle,
} from '../animation/calculate-animation';
import {
  calculateFadeInOpacity,
  calculateFadeOutOpacity,
} from '../video/calculate-fade';
import { BlurItem } from './blur-item-type';

export const BlurLayer = ({
  item,
  zIndex,
}: {
  item: BlurItem;
  zIndex?: number;
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const opacity = useMemo(() => {
    const inOpacity = calculateFadeInOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeInDurationInSeconds: item.fadeInDurationInSeconds,
      framesPerSecond: fps,
    });
    const outOpacity = calculateFadeOutOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeOutDurationInSeconds: item.fadeOutDurationInSeconds,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
    });
    return inOpacity * outOpacity * item.opacity;
  }, [
    item.visualFadeWindow,
    item.fadeInDurationInSeconds,
    fps,
    frame,
    item.opacity,
    item.durationInFrames,
    item.fadeOutDurationInSeconds,
  ]);

  const style: React.CSSProperties = useMemo(() => {
    return {
      position: 'absolute',
      left: item.left,
      top: item.top,
      width: item.width,
      height: item.height,
      opacity,
      borderRadius: item.borderRadius,
      overflow: 'hidden', // 👈 backdrop-filter + borderRadius 조합에 필요
      transform: `rotate(${item.rotation}deg)`,
      backdropFilter: `blur(${item.blurAmount}px)`,
      WebkitBackdropFilter: `blur(${item.blurAmount}px)`,
      backgroundColor: 'rgba(255, 255, 255, 0.01)',
      zIndex,
      isolation: zIndex !== undefined ? 'isolate' : undefined,
    };
  }, [
    item.height,
    item.left,
    item.top,
    item.width,
    opacity,
    item.borderRadius,
    item.rotation,
    item.blurAmount,
    zIndex,
  ]);

  // Animation 스타일 계산
  const animationStyle = useMemo(() => {
    const hasAnimations = item.animations && item.animations.length > 0;
    const hasPositionKeyframes =
      item.positionKeyframes && item.positionKeyframes.length > 0;
    if (!hasAnimations && !hasPositionKeyframes) {
      return {};
    }
    return calculateAnimationsStyle({
      animations: item.animations ?? [],
      currentFrame: frame,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
      positionKeyframes: item.positionKeyframes,
    });
  }, [
    item.animations,
    item.positionKeyframes,
    frame,
    fps,
    item.durationInFrames,
  ]);

  // style에 animation 적용
  const finalStyle = useMemo(() => {
    return mergeAnimationStyle(style, animationStyle);
  }, [style, animationStyle]);

  return <div style={finalStyle}></div>;
};
